import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { LanguageToggle } from "@/components/LanguageToggle";
import { AccountDropdown } from "@/components/AccountDropdown";
import { PurchaseModal } from "@/components/PurchaseModal";
import { useT, getLocale } from "@/lib/i18n";

// French: singular for 0 and 1, U+00A0 after the number; below 640 px « de rapport » drops.
const WalletPillLabel = ({ credits }) => {
  if (getLocale() !== "fr") return <>{credits === 0 ? "0 report credits · Buy" : `${credits} report credit${credits === 1 ? "" : "s"}`}</>;
  const noun = credits <= 1 ? "crédit" : "crédits";
  return (
    <span data-testid="wallet-pill-label">
      {credits}{"\u00a0"}{noun}<span className="hidden sm:inline"> de rapport</span>{credits === 0 ? " · Acheter" : ""}
    </span>
  );
};
import { Layers, ExternalLink, Wallet, Inbox, Ticket } from "lucide-react";
import { api } from "@/lib/api";

const MARKETING_SITE_URL = "https://futureenergyventure.com/";

export const Header = () => {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const t = useT();
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const [lockedProjects, setLockedProjects] = useState([]);
  // Admin-only: poll unread feedback count. `/api/admin/nav` returns 403 for
  // non-admins so we hide the badge silently when the call fails.
  const [adminNav, setAdminNav] = useState(null);
  useEffect(() => {
    if (!user) { setAdminNav(null); return; }
    let cancelled = false;
    const pull = async () => {
      try {
        const { data } = await api.get("/admin/nav");
        if (!cancelled) setAdminNav(data);
      } catch { if (!cancelled) setAdminNav(null); }
    };
    pull();
    const t = setInterval(pull, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, [user]);

  const openPurchase = async () => {
    // Fetch the user's projects so the $149 flow can require project selection.
    try {
      const { data } = await api.get("/projects");
      setLockedProjects((data || []).filter((p) => !p.paid));
    } catch {
      setLockedProjects([]);
    }
    setPurchaseOpen(true);
  };

  return (
    <header
      className="sticky top-0 z-40"
      style={{ background: "rgba(255,255,255,0.9)", backdropFilter: "blur(12px)", borderBottom: "1px solid var(--hairline)" }}
      data-testid="site-header"
    >
      <div className="max-w-[1280px] mx-auto px-3 sm:px-6 lg:px-10 py-4 flex items-center justify-between gap-2">
        <a href={MARKETING_SITE_URL} className="flex items-center gap-3 shrink-0" data-testid="logo-link">
          <Logo size={30} />
          <div className="leading-tight hidden sm:block md:hidden lg:block">
            <div className="font-display font-bold text-[15px] tracking-tight" style={{ color: "var(--charcoal)" }}>
              Future Energy Venture
            </div>
            <div className="overline" style={{ fontSize: "0.62rem", letterSpacing: "0.18em" }}>
              Power Configurator
            </div>
          </div>
        </a>

        <nav className="hidden md:flex items-center gap-4 lg:gap-6 xl:gap-8">
          <a href={MARKETING_SITE_URL} className="text-sm font-medium hidden lg:flex items-center gap-1.5 transition-colors hover:opacity-70" style={{ color: "var(--muted)" }} data-testid="nav-back-to-site">
            <ExternalLink size={12} /> {t("nav.back_to_site")}
          </a>
          <Link to="/configurator" className="text-sm font-medium" style={{ color: "var(--charcoal)" }} data-testid="nav-configurator">{t("nav.configurator")}</Link>
          <a href="/#methodology" className="text-sm font-medium hidden lg:inline" style={{ color: "var(--charcoal)" }} data-testid="nav-methodology">{t("nav.methodology")}</a>
          <a href="/pricing" className="text-sm font-medium" style={{ color: "var(--charcoal)" }} data-testid="nav-pricing">{t("nav.pricing")}</a>
          {user && (
            <Link to="/projects" className="text-sm font-medium flex items-center gap-1.5" style={{ color: "var(--charcoal)" }} data-testid="nav-projects">
              <Layers size={14} /> {t("nav.projects")}
            </Link>
          )}
          {adminNav && (
            <Link to="/admin/feedback"
                  className="text-sm font-medium hidden lg:flex items-center gap-1.5 relative"
                  style={{ color: "var(--charcoal)" }}
                  data-testid="nav-admin-feedback">
              <Inbox size={14} /> Inbox
              {adminNav.new_feedback > 0 && (
                <span
                  data-testid="admin-unread-badge"
                  className="inline-flex items-center justify-center rounded-full text-[10px] font-bold"
                  style={{
                    background: "#C0392B", color: "#fff",
                    minWidth: 18, height: 18, padding: "0 5px", lineHeight: "18px",
                  }}
                  aria-label={`${adminNav.new_feedback} new feedback items`}
                >
                  {adminNav.new_feedback > 99 ? "99+" : adminNav.new_feedback}
                </span>
              )}
            </Link>
          )}
          {adminNav && (
            <Link to="/admin/promos"
                  className="text-sm font-medium hidden lg:flex items-center gap-1.5"
                  style={{ color: "var(--charcoal)" }}
                  data-testid="nav-admin-promos">
              <Ticket size={14} /> Promos
            </Link>
          )}
        </nav>

        <div className="flex items-center gap-1.5 sm:gap-3 min-w-0">
          <LanguageToggle />
          {user ? (
            <>
              <button
                onClick={openPurchase}
                className="pill inline-flex items-center gap-1.5 text-xs sm:text-sm cursor-pointer text-left leading-tight max-w-[120px] sm:max-w-none"
                style={{ background: (user.credits || 0) > 0 ? "var(--paper)" : "#FFF6DE", color: (user.credits || 0) > 0 ? "var(--charcoal)" : "#5B4A00", border: "none" }}
                data-testid="wallet-pill"
              >
                <Wallet size={13} />
                <WalletPillLabel credits={user.credits || 0} />
              </button>
              <AccountDropdown onBuyClick={openPurchase} />
            </>
          ) : (
            <>
              <button onClick={() => navigate("/login")} className="btn-secondary !py-2 !px-2.5 sm:!px-4 text-xs sm:text-sm leading-tight whitespace-nowrap hidden sm:inline-flex" data-testid="signin-btn">{t("nav.signin")}</button>
              <button onClick={() => navigate("/configurator")} className={`btn-primary !py-2 !px-2.5 sm:!px-4 text-xs sm:text-sm leading-tight text-center sm:whitespace-nowrap ${getLocale() === "fr" ? "max-w-[84px] sm:max-w-none" : "whitespace-nowrap"}`} data-testid="cta-start-header"><span className="sm:hidden md:inline xl:hidden" data-testid="cta-start-header-short">{t("nav.start_cta_short")}</span><span className="hidden sm:inline md:hidden xl:inline">{t("nav.start_cta")}</span></button>
            </>
          )}
        </div>
      </div>

      <PurchaseModal
        open={purchaseOpen}
        onClose={() => setPurchaseOpen(false)}
        lockedProjects={lockedProjects}
        onPurchaseComplete={() => { checkAuth && checkAuth(); setPurchaseOpen(false); navigate("/projects"); }}
      />
    </header>
  );
};
